import { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { trackingIds } from '../../../../tracking-ids';
import { useActiveFilters } from '../../hooks/use-active-filters';
import { useCallIntelLocations } from '../../hooks/use-call-intel-locations';
import { FilterNotifications } from '../atoms/filter-notifications';
import { MIN_SEARCH_LENGTH } from '../constants';
import { CallIntelFiltersProvider } from '../context/call-intel-filters.provider';
import { AiEventFilterTray } from '../molecules/ai-event-filter-tray';
import { AiOutcomeFilterTray } from '../molecules/ai-outcome-filter-tray';
import { AppointmentTypeFilterTray } from '../molecules/appointment-type-filter-tray';
import { CallDirectionFilterTray } from '../molecules/call-direction-filter-tray';
import { CategoryFilterTray } from '../molecules/category-filter-tray';
import { ContactTypeFilterChip } from '../molecules/contact-type-filter/contact-type-filter-chip';
import { ContactTypeFilterTray } from '../molecules/contact-type-filter/contact-type-filter-tray';
import { FilterTrayToggle } from '../molecules/filter-tray-section/filter-tray-toggle';
import { HideNonAnalyzedCallsFilterTray } from '../molecules/hide-non-analyzed-calls-filter-tray';
import { LocationFilterChip } from '../molecules/location-filter/location-filter-chip';
import { LocationFilterTray } from '../molecules/location-filter/location-filter-tray';
import { OfficeHoursFilterTray } from '../molecules/office-hours-filter-tray';
import { OfficeHoursResetBanner } from '../molecules/office-hours-reset-banner';
import { OfficeNumberFilterTray } from '../molecules/office-number-filter-tray';
import { OfficeUserFilterTray } from '../molecules/office-user-filter/office-user-filter-tray';
import { SchedulingOutcomeFilterChip } from '../molecules/scheduling-outcome-filter/scheduling-outcome-filter-chip';
import { SchedulingOutcomeFilterTray } from '../molecules/scheduling-outcome-filter/scheduling-outcome-filter-tray';
import { SearchFilter } from '../molecules/search-filter';
import { SentimentFilterTray } from '../molecules/sentiment-filter-tray';
import { ServiceQualityFilterTray } from '../molecules/service-quality-filter-tray';
import { TimePeriodFilterChip } from '../molecules/time-period-filter/time-period-filter-chip';
import { TimePeriodFilterTray } from '../molecules/time-period-filter/time-period-filter-tray';
import { TranscriptSearchFilter } from '../molecules/transcript-search-filter';
import { FilterTray } from './filter-tray';

export interface CallIntelRecordsFiltersProps {
  containerStyles?: Interpolation<Theme>;
  isLoading?: boolean;
  officeUsers?: Record<string, string>;
  officeNumbers?: string[];
  hideFilters?: (keyof CallIntelTypes.Filters)[];
  hideChipFilters?: (keyof CallIntelTypes.Filters)[] | 'all';
  trayNotificationMessage?: ReactNode;
  nonAnalyzedCallsCount?: number;
  onApplyingFilters?: () => void;
  showOnlyTrayFilters?: boolean;
  exportAction?: {
    show: boolean;
    onClick: () => void;
    trackingId?: string;
  };
  searchAction?: {
    show: boolean;
    searchValue: string;
    onChange: (value: string, debounced?: boolean) => void;
    trackingId?: string;
  };
  transcriptSearchAction?: {
    isActive: boolean;
    onChange: (value: string, debounced?: boolean) => void;
    onActiveChange: (isActive: boolean) => void;
    onSearchTypeChange: (searchType: CallIntelTypes.SearchTypeEnum) => void;
    show: boolean;
    searchType: CallIntelTypes.SearchTypeEnum;
    trackingId?: string;
    value: string;
  };
}

export const CallIntelRecordsFilters: FC<CallIntelRecordsFiltersProps> = ({
  containerStyles,
  isLoading,
  officeUsers,
  officeNumbers,
  nonAnalyzedCallsCount,
  hideFilters = [],
  hideChipFilters = [],
  onApplyingFilters,
  searchAction,
  showOnlyTrayFilters,
  exportAction,
  transcriptSearchAction,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { isMultiLocation } = useCallIntelLocations();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const { isSmallMode } = useIsSmallMode();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);

  const isTranscriptSearchActive = transcriptSearchAction?.isActive ?? false;
  const isTranscriptSearchEnabled = transcriptSearchAction?.show ?? false;

  const activeSearchConfig = useMemo(
    () =>
      isTranscriptSearchEnabled && transcriptSearchAction
        ? { show: transcriptSearchAction.show, trackingId: transcriptSearchAction.trackingId }
        : { show: searchAction?.show ?? false, trackingId: searchAction?.trackingId },
    [isTranscriptSearchEnabled, transcriptSearchAction, searchAction]
  );

  const { filters } = useActiveFilters();

  const hasStaffPerformanceAnalyticsAccess = useHasCallIntelAclAccess(
    [Permission.CALL_INTELLIGENCE_STAFF_PERFORMANCE_ANALYTICS],
    {
      locations: filters.locations,
      allowWeaveOverride: true,
      allowDemoOverride: true,
      requireAllLocations: true,
    }
  );

  const [isAirEventsUiEnabled] = CallIntelligenceOrgWetags.useWetag('call-intel-air-events-ui');
  const isOfficeHourFilterEnabled = getFeatureFlagValue(featureFlags.enableOfficeHourFilter);

  const shouldHideFilter = useCallback(
    (filterKey: keyof CallIntelTypes.Filters) => hideFilters?.includes(filterKey) ?? false,
    [hideFilters]
  );

  const shouldHideChipFilter = useCallback(
    (filterKey: keyof CallIntelTypes.Filters) =>
      hideChipFilters === 'all' || ([...hideChipFilters, ...hideFilters]?.includes(filterKey) ?? false),
    [hideChipFilters, hideFilters]
  );

  const expandableSearchMode = useMemo(
    () => (isSmallMode ? { isExpanded: isSearchExpanded, onExpandStateChange: setIsSearchExpanded } : undefined),
    [isSmallMode, isSearchExpanded]
  );

  const shouldHideChips = showOnlyTrayFilters || isSearchExpanded || isTranscriptSearchActive;

  const renderChipsFilters = useMemo(() => {
    if (shouldHideChips) {
      return [];
    }
    return [
      isMultiLocation && !shouldHideChipFilter('locations') && <LocationFilterChip />,
      !shouldHideChipFilter('startDate') && !shouldHideChipFilter('endDate') && <TimePeriodFilterChip />,
      !shouldHideChipFilter('contactTypes') && <ContactTypeFilterChip />,
      !shouldHideChipFilter('schedulingOutcomes') && <SchedulingOutcomeFilterChip />,
    ]
      .filter(Boolean)
      .slice(0, isSmallMode ? 2 : undefined);
  }, [shouldHideChips, isMultiLocation, shouldHideChipFilter, isSmallMode]);

  const actionItems = useMemo<ActionGroupItem[]>(() => {
    return [
      {
        label: t('Search'),
        iconName: 'search',
        show: isSmallMode && activeSearchConfig.show,
        onClick: () => setIsSearchExpanded((prev) => !prev),
        trackingId: activeSearchConfig.trackingId ?? trackingIds.callIntel.allCallRecordingsSearch,
      },
      {
        label: t('Download Data'),
        iconName: 'download',
        trackingId: exportAction?.trackingId,
        onClick: exportAction?.onClick,
        show: exportAction?.show ?? false,
      },
    ];
  }, [
    activeSearchConfig.show,
    activeSearchConfig.trackingId,
    exportAction?.onClick,
    exportAction?.show,
    exportAction?.trackingId,
    isSmallMode,
    t,
  ]);

  const showFullSearch = isSmallMode ? isSearchExpanded : activeSearchConfig.show;

  return (
    <CallIntelFiltersProvider isLoading={isLoading} onApplyingFilters={onApplyingFilters}>
      <div css={[styles.container, containerStyles]}>
        {renderChipsFilters}

        <div
          css={styles.actionsWrapper({
            hasChipsFilters: renderChipsFilters.length > 0,
          })}
        >
          {showFullSearch &&
            (isTranscriptSearchEnabled && transcriptSearchAction ? (
              <TranscriptSearchFilter
                expandableMode={expandableSearchMode}
                onActiveChange={transcriptSearchAction.onActiveChange}
                onSearchTypeChange={transcriptSearchAction.onSearchTypeChange}
                onChange={transcriptSearchAction.onChange}
                value={transcriptSearchAction.value}
                searchType={transcriptSearchAction.searchType}
                trackingId={transcriptSearchAction.trackingId}
                wrapperStyles={styles.transcriptSearchBar({
                  isExpanded: isTranscriptSearchActive,
                })}
              />
            ) : (
              <SearchFilter
                minSearchLength={MIN_SEARCH_LENGTH}
                onChange={searchAction?.onChange}
                value={searchAction?.searchValue}
                trackingId={searchAction?.trackingId}
                placeholder={t('Search Name or Phone Number')}
                wrapperStyles={styles.searchBar(isSearchExpanded)}
                expandableMode={expandableSearchMode}
              />
            ))}
          {!isSearchExpanded && !isTranscriptSearchActive && <ActionGroup items={actionItems} maxVisibleItems={1} />}
          {!isTranscriptSearchActive && <FilterTrayToggle css={styles.filterButton({ isSearchExpanded })} />}
        </div>
      </div>

      <FilterTray>
        {isMultiLocation && !shouldHideFilter('locations') && <LocationFilterTray />}
        {!shouldHideFilter('startDate') && !shouldHideFilter('endDate') && <TimePeriodFilterTray />}
        {!shouldHideFilter('contactTypes') && <ContactTypeFilterTray />}
        {!shouldHideFilter('callDirection') && <CallDirectionFilterTray />}
        {hasStaffPerformanceAnalyticsAccess && !shouldHideFilter('officeUsers') && (
          <OfficeUserFilterTray officeUsers={officeUsers} />
        )}
        {!shouldHideFilter('aiOutcomes') && (isAirEventsUiEnabled ? <AiEventFilterTray /> : <AiOutcomeFilterTray />)}
        {!shouldHideFilter('schedulingOutcomes') && <SchedulingOutcomeFilterTray />}
        {!shouldHideFilter('sentiments') && <SentimentFilterTray />}
        {!shouldHideFilter('serviceQualityFlags') && <ServiceQualityFilterTray />}
        {!shouldHideFilter('categories') && <CategoryFilterTray />}
        {!shouldHideFilter('appointmentTypes') && <AppointmentTypeFilterTray />}
        {!shouldHideFilter('officeNumbers') && <OfficeNumberFilterTray officeNumbers={officeNumbers} />}
        {isOfficeHourFilterEnabled && !shouldHideFilter('officeHours') && (
          <OfficeHoursFilterTray validateCallDirection />
        )}
        {!shouldHideFilter('hideNonAnalyzedCalls') && (
          <HideNonAnalyzedCallsFilterTray nonAnalyzedCallsCount={nonAnalyzedCallsCount} />
        )}
      </FilterTray>
      <FilterNotifications>
        <OfficeHoursResetBanner customStyles={styles.officeHoursResetBanner} />
      </FilterNotifications>
    </CallIntelFiltersProvider>
  );
};

interface ActionGroupItem {
  label: string;
  iconName: IconName;
  show: boolean;
  onClick?: () => void;
  trackingId?: string;
}

const ActionGroup: FC<{ items: ActionGroupItem[]; maxVisibleItems: number }> = ({ items, maxVisibleItems = 1 }) => {
  const filteredItems = items.filter((item) => item.show);
  const { getTriggerProps, getMenuProps, getItemProps } = usePopoverMenu();
  const { t } = useTranslation('call-intelligence');

  if (filteredItems.length === 0) {
    return null;
  }

  if (filteredItems.length <= maxVisibleItems) {
    return (
      <>
        {filteredItems.map((item) => (
          <Button
            size='large'
            variant='secondary'
            iconName={item.iconName}
            key={item.label}
            onClick={item.onClick}
            trackingId={item.trackingId}
            css={styles.actionIconButton}
            hoverLabel={item.label}
          />
        ))}
      </>
    );
  }

  return (
    <>
      <Button
        size='large'
        variant='secondary'
        iconName='more'
        trackingId={trackingIds.callIntel.filterActionsMenu}
        {...getTriggerProps()}
        css={styles.actionIconButton}
        hoverLabel={t('View Actions')}
      />
      <PopoverMenu {...getMenuProps()}>
        {filteredItems.map(({ iconName, label, onClick, trackingId }, index) => (
          <PopoverMenuItem
            key={index}
            Icon={() => <Icon name={iconName} size={18} />}
            {...getItemProps({ index, onClick })}
            trackingId={trackingId}
          >
            {label}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>
    </>
  );
};

const styles = {
  container: css`
    display: flex;
    gap: ${theme.spacing(2)};
    flex: 1;
    flex-wrap: wrap;
  `,
  actionsWrapper: ({ hasChipsFilters }: { hasChipsFilters: boolean }) => css`
    display: flex;
    gap: ${theme.spacing(2)};
    flex: 1;
    justify-content: flex-end;
    @container (max-width: ${breakpoints.xsmall.max}px) {
      flex-direction: row-reverse;
      justify-content: ${hasChipsFilters ? 'flex-end' : 'flex-start'};
    }
  `,
  searchBar: (isSearchExpanded: boolean) => css`
    min-width: 300px;
    @container (max-width: ${breakpoints.small.max}px) {
      margin-right: auto;
      width: ${isSearchExpanded ? '100%' : 'auto'};
    }
  `,
  transcriptSearchBar: ({ isExpanded }: { isExpanded: boolean }) => css`
    min-width: 300px;
    ${isExpanded ? 'width: 100%;' : ''}
    @container (max-width: ${breakpoints.small.max}px) {
      margin-right: auto;
      width: ${isExpanded ? '100%' : 'auto'};
    }
  `,
  filterButton: ({ isSearchExpanded }: { isSearchExpanded?: boolean }) => css`
    justify-self: flex-end;
    display: ${isSearchExpanded ? 'none' : 'block'};
    @container (max-width: ${breakpoints.xsmall.max}px) {
      border: 1px solid ${theme.colors.neutral50};
    }
  `,
  actionIconButton: css`
    border: 1px solid ${theme.colors.neutral50};
  `,
  officeHoursResetBanner: css`
    margin-top: ${theme.spacing(2)};

    @container (max-width: ${breakpoints.xsmall.max}px) {
      margin-top: ${theme.spacing(0)};
    }
  `,
};
